import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const BUCKET = 'site-images'

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// ── Images ─────────────────────────────────────────────────────────────────
export async function uploadImage(slotId: string, blob: Blob): Promise<string> {
  const fileName = `${slotId.replace(/\./g, '_')}.jpg`
  const { error } = await supabase.storage.from(BUCKET).upload(fileName, blob, {
    contentType: 'image/jpeg', upsert: true,
  })
  if (error) throw error
  return getStorageUrl(fileName)
}

export function getStorageUrl(fileName: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName)
  return data.publicUrl
}

export function imageSlotToFileName(slotId: string): string {
  return `${slotId.replace(/\./g, '_')}.jpg`
}

// ── Contenu texte + images (table site_content) ───────────────────────────
export async function loadAllContent(): Promise<Record<string, string>> {
  const { data, error } = await supabase.from('site_content').select('key, value')
  if (error) { console.warn('Supabase loadAllContent error:', error.message); return {} }
  const result: Record<string, string> = {}
  for (const row of (data ?? [])) result[row.key] = row.value
  return result
}

export async function saveContent(key: string, value: string): Promise<void> {
  const { error } = await supabase.from('site_content').upsert({ key, value, updated_at: new Date().toISOString() })
  if (error) console.warn('Supabase saveContent error:', error.message)
}

// ── Statistiques ──────────────────────────────────────────────────────────
export async function recordVisit(): Promise<void> {
  try {
    const ua = navigator.userAgent
    let device = 'desktop'
    if (/Mobi|Android/i.test(ua)) device = 'mobile'
    else if (/Tablet|iPad/i.test(ua)) device = 'tablet'

    const referrer = document.referrer
      ? new URL(document.referrer).hostname
      : 'direct'

    await supabase.from('site_visits').insert({
      page: '/',
      referrer,
      device,
      screen: `${window.screen.width}x${window.screen.height}`,
    })
  } catch { /* silencieux */ }
}

export interface VisitStats {
  total: number
  today: number
  thisWeek: number
  thisMonth: number
  byDay: { date: string; count: number }[]
  byDevice: { device: string; count: number }[]
  byReferrer: { referrer: string; count: number }[]
}

export async function loadStats(): Promise<VisitStats> {
  const now = new Date()
  const todayStr = now.toISOString().split('T')[0]
  const weekAgo = new Date(now.getTime() - 7 * 24 * 3600 * 1000).toISOString()
  const monthAgo = new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString()

  const { data: all } = await supabase.from('site_visits').select('visited_at, device, referrer')
  const rows: { visited_at: string; device: string; referrer: string }[] = all ?? []

  const total = rows.length
  const today = rows.filter(r => r.visited_at.startsWith(todayStr)).length
  const thisWeek = rows.filter(r => r.visited_at >= weekAgo).length
  const thisMonth = rows.filter(r => r.visited_at >= monthAgo).length

  // Par jour (30 derniers jours)
  const dayMap: Record<string, number> = {}
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 24 * 3600 * 1000)
    dayMap[d.toISOString().split('T')[0]] = 0
  }
  rows.filter(r => r.visited_at >= monthAgo).forEach(r => {
    const day = r.visited_at.split('T')[0]
    if (dayMap[day] !== undefined) dayMap[day]++
  })
  const byDay = Object.entries(dayMap).map(([date, count]) => ({ date, count }))

  // Par appareil
  const devMap: Record<string, number> = {}
  rows.forEach(r => { devMap[r.device] = (devMap[r.device] ?? 0) + 1 })
  const byDevice = Object.entries(devMap)
    .map(([device, count]) => ({ device, count }))
    .sort((a, b) => b.count - a.count)

  // Par source
  const refMap: Record<string, number> = {}
  rows.forEach(r => { const k = r.referrer || 'direct'; refMap[k] = (refMap[k] ?? 0) + 1 })
  const byReferrer = Object.entries(refMap)
    .map(([referrer, count]) => ({ referrer, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)

  return { total, today, thisWeek, thisMonth, byDay, byDevice, byReferrer }
}
